import React from 'react';
import { Link } from 'react-router-dom';

export type WbsLevel1Status = 'ON_TRACK' | 'AT_RISK' | 'DELAY';

export type WbsLevel1Row = {
    id: string;

    /** ✅ Code 컬럼 */
    code?: string;

    /** ✅ Title 컬럼 */
    title: string;

    /** 담당(선택) */
    owner?: string;

    /** ✅ Weight 컬럼 (0~100, 1단계 합 100 권장) */
    weightPct: number;

    /** ✅ Schedule 컬럼 */
    scheduleText?: string; // 예: "2025-10-27 ~ 2026-08-30"
    startISO?: string;
    finishISO?: string;

    /** ✅ 계획/실적 컬럼 */
    pvPct?: number; // 0~100
    evPct: number; // 0~100

    /** ✅ Status 컬럼 */
    status?: WbsLevel1Status;
};

function clamp(v: number) {
    return Math.max(0, Math.min(100, v));
}

function toneByStatus(s?: WbsLevel1Status) {
    switch (s) {
        case 'DELAY':
            return { badge: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' };
        case 'AT_RISK':
            return { badge: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' };
        default:
            return { badge: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' };
    }
}

function statusText(s?: WbsLevel1Status) {
    switch (s) {
        case 'DELAY':
            return '지연';
        case 'AT_RISK':
            return '주의';
        default:
            return '양호';
    }
}

function progressTone(pv: number | undefined, ev: number) {
    if (typeof pv !== 'number') {
        return {
            chip: 'bg-slate-50 text-slate-700 border-slate-200',
            fill: 'bg-sky-600',
            deltaText: '기준 없음',
        };
    }

    const delta = ev - pv;
    if (delta <= -10) {
        return {
            chip: 'bg-rose-50 text-rose-700 border-rose-200',
            fill: 'bg-rose-600',
            deltaText: `${Math.abs(delta).toFixed(0)}%p 뒤처짐`,
        };
    }
    if (delta < 0) {
        return {
            chip: 'bg-amber-50 text-amber-800 border-amber-200',
            fill: 'bg-amber-500',
            deltaText: `${Math.abs(delta).toFixed(0)}%p 차이`,
        };
    }
    return {
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        fill: 'bg-emerald-600',
        deltaText: delta === 0 ? '계획 일치' : `${delta.toFixed(0)}%p 앞섬`,
    };
}

function fmtSchedule(row: WbsLevel1Row) {
    if (row.scheduleText) return row.scheduleText;
    if (row.startISO && row.finishISO) return `${row.startISO} ~ ${row.finishISO}`;
    if (row.startISO) return `${row.startISO} ~`;
    if (row.finishISO) return `~ ${row.finishISO}`;
    return '—';
}

function PlanActualBar({ pv, ev }: { pv?: number; ev: number }) {
    const pvClamped = typeof pv === 'number' ? clamp(pv) : undefined;
    const evClamped = clamp(ev);
    const tone = progressTone(pvClamped, evClamped);

    const title =
        typeof pvClamped === 'number'
            ? `계획(PV): ${pvClamped.toFixed(1)}% · 실적(EV): ${evClamped.toFixed(1)}%`
            : `실적(EV): ${evClamped.toFixed(1)}%`;

    return (
        <div className="w-full" title={title} aria-label={title}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-xs tabular-nums text-gray-600">
                    <span>{typeof pvClamped === 'number' ? `PV ${pvClamped.toFixed(0)}%` : 'PV —'}</span>
                    <span className="font-semibold text-slate-800">{`EV ${evClamped.toFixed(0)}%`}</span>
                </div>
                <span className={['inline-flex rounded-full border px-2.5 py-1 text-xs font-medium', tone.chip].join(' ')}>
                    {tone.deltaText}
                </span>
            </div>

            <div className="space-y-2">
                <div className="grid grid-cols-[32px_minmax(0,1fr)_48px] items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">PV</span>
                    <div
                        className="h-[14px] overflow-hidden rounded-full bg-slate-100"
                    >
                        {typeof pvClamped === 'number' && (
                            <div className="h-full rounded-full bg-sky-300" style={{ width: `${pvClamped}%` }} />
                        )}
                    </div>
                    <span className="text-right text-xs tabular-nums text-slate-500">
                        {typeof pvClamped === 'number' ? `${pvClamped.toFixed(0)}%` : '—'}
                    </span>
                </div>

                <div className="grid grid-cols-[32px_minmax(0,1fr)_48px] items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700">EV</span>
                    <div className="h-[14px] overflow-hidden rounded-full bg-slate-100">
                        <div className={['h-full rounded-full', tone.fill].join(' ')} style={{ width: `${evClamped}%` }} />
                    </div>
                    <span className="text-right text-xs font-semibold tabular-nums text-slate-700">
                        {evClamped.toFixed(0)}%
                    </span>
                </div>
            </div>
        </div>
    );
}

export default function WbsLevel1Summary({
    rows,
    title = 'WBS 1단계 현황',
    wbsTo,
    wbsListVersion,
}: {
    rows: WbsLevel1Row[];
    title?: string;

    /** ✅ "WBS 열기" 버튼 이동 경로 (부모에서 projectId 포함해서 넘겨주세요) */
    wbsTo: string;
    wbsListVersion?: number | null;
}) {
    const totalWeight = rows.reduce((acc, r) => acc + (r.weightPct || 0), 0);

    return (
        <div className="rounded-2xl border bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
                <div className="min-w-0">
                    <div className="text-base font-semibold text-slate-900">{title}</div>
                    <div className="mt-1 text-sm text-gray-500">
                        총 {rows.length}개 · 가중치 합 {totalWeight.toFixed(1)}% ·{' '}
                        {wbsListVersion ? `WBS V${wbsListVersion}` : '-'}
                    </div>
                </div>

                {/* ✅ 우측 끝 "WBS 열기" */}
                <Link
                    to={wbsTo}
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                    WBS 열기
                    <span aria-hidden className="text-gray-400">
                        →
                    </span>
                </Link>
            </div>

            <div className="p-4 overflow-x-auto">
                {rows.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                        아직 게시된 WBS가 없습니다. WBS를 먼저 입력하세요.
                    </div>
                ) : (
                    <table className="min-w-[1040px] w-full table-fixed text-sm">
                        <colgroup>
                            <col className="w-[90px]" />
                            <col className="w-[24%]" />
                            <col className="w-[90px]" />
                            <col className="w-[170px]" />
                            <col className="w-[33%]" />
                            <col className="w-[110px]" />
                            <col className="w-[140px]" />
                        </colgroup>
                        <thead className="text-sm text-gray-500">
                            <tr>
                                <th className="text-left font-medium px-2 py-1">코드</th>
                                <th className="text-left font-medium px-2 py-1">작업 내역</th>
                                <th className="text-right font-medium px-2 py-1">가중치</th>
                                <th className="text-left font-medium px-2 py-1">일정</th>
                                <th className="text-left font-medium px-2 py-1">진행 현황</th>
                                <th className="text-left font-medium px-2 py-1">상태</th>
                                <th className="text-left font-medium px-2 py-1">담당회사(조직)</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {rows.map((r) => {
                                const t = toneByStatus(r.status);
                                const pv = typeof r.pvPct === 'number' ? clamp(r.pvPct) : undefined;
                                const ev = clamp(r.evPct);

                                return (
                                    <tr key={r.id} className="align-top">
                                        <td className="py-3 pr-3 text-gray-600 tabular-nums">{r.code ?? '—'}</td>
                                        <td className="py-3 pr-3">
                                            <div className="text-base font-medium text-gray-900">{r.title}</div>
                                        </td>
                                        <td className="py-3 pr-3 text-right tabular-nums font-medium text-gray-900">
                                            {r.weightPct.toFixed(1)}%
                                        </td>
                                        <td className="py-3 pr-3 text-gray-700 tabular-nums">{fmtSchedule(r)}</td>
                                        <td className="py-3 pr-3">
                                            <PlanActualBar pv={pv} ev={ev} />
                                        </td>
                                        <td className="py-3">
                                            <span
                                                className={[
                                                    'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold shadow-sm',
                                                    t.badge,
                                                ].join(' ')}
                                            >
                                                <span
                                                    className={['inline-block h-2.5 w-2.5 rounded-full ring-2 ring-white', t.dot].join(' ')}
                                                />
                                                {statusText(r.status)}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-3">{r.owner}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div className="mt-3 text-sm text-gray-500">
                    * Progress는 계획(PV)과 실적(EV)을 평행 바 2줄로 표시합니다. (Hover 시 PV/EV 수치 확인)
                </div>
            </div>
        </div>
    );
}
